import React, { useMemo } from "react";
import { useSelector } from "react-redux";

import * as genentechCopayFieldNames from "./form/fieldNames";
import { getGenentechCopayFormInitialValues } from "./form/initialValues";
import genentechCopayFormJson from "./form/GenentechCopayFormJson.json";
import { resolveGenentechCopayFormJson } from "./form/facilityNpiAutofill";
import useBaseEEConfiguration from "../../hooks/useBaseEEConfiguration";
import renderReactFromJson from "../../utils/renderReactFromJson";
import { TEMPLATE_NAMES } from "../../templateNames";
import { useGetSetting } from "../../../../../hooks/getSetting";
import { SaveButton } from "../../header";
import { CompleteFormButton } from "../../CompleteFormButton";
import { HEADER_SUBMIT_BUTTON_TYPES } from "../../constants";
import { onSaveGenentechForm } from "../freeDrug/useGenentechFreeDrugConfiguration";
import { EEsignatureStatuses } from "../../../../../constant/EEsignatureStatuses";
import { SubTitleStyled } from "../../../PatientApplications/enrollTab/submission/ApprovedResult";
import { selectSelectedAssistanceProgramById } from "../../../../../store/selector";
import { dateFormatter } from "../../../../../utils/date";

// Preserved verbatim from the pre-JSON genentechCopayConfigurations: the approved-result subtitle
// (with the optional debit-card line). Reads the selected AP for its support phone.
const renderApplicationApprovedSubtitle = (t, assignDebitcard) => {
  const selectedAssistanceProgramById = useSelector(selectSelectedAssistanceProgramById);
  const phone = selectedAssistanceProgramById?.phone1;
  return (
    <div style={{ width: 540, marginBottom: "1rem" }}>
      <SubTitleStyled style={{ whiteSpace: "normal", lineHeight: "24px", display: "inline" }}>
        {t("patientApplications.enroll.enroll_result.application_approved_subtitle")}
      </SubTitleStyled>
      {assignDebitcard && (
        <SubTitleStyled style={{ whiteSpace: "normal", lineHeight: "24px", display: "inline" }}>
          {t("patientApplications.enroll.enroll_result.application_approved_assign_debitcard_text", { phone })}
        </SubTitleStyled>
      )}
    </div>
  );
};

// Sidebar validation groups the base can't infer from the section name alone: the eligibility section
// validates the questionnaire field, and the admin-only support-options item validates assistanceType.
// patient / insurance / physician / facility fall back to [sectionName].
const validationFieldsBySidebarItem = {
  eligibility_questionnaire: ["eligibilityQuestionnaire"],
  "support-options": ["assistanceType"],
  // facility folds into this one item (its section is hideInSidebar), so it validates both.
  physician: ["physician", "facility"]
};

export const useGenentechCopayConfiguration = () => {
  const vendorName = "genentech";
  const eePlanType = "copay";
  const rootPath = "application_form_editor.genentech.copay";

  // Preserve a tenant-configured default facility NPI: when fe.defaultFacilityNpis['genentech-copay'] is set, use
  // the form JSON variant that doesn't auto-fill facility NPI on selection, so choosing a facility can't overwrite
  // the default. Keyed on the NPI string so the derived form is stable across renders.
  const [defaultFacilityNpis] = useGetSetting(["fe.defaultFacilityNpis"]);
  const gneCopayDefaultNpi = defaultFacilityNpis?.[TEMPLATE_NAMES.GENENTECH_COPAY];
  const formJson = useMemo(
    () => resolveGenentechCopayFormJson(genentechCopayFormJson, gneCopayDefaultNpi),
    [gneCopayDefaultNpi]
  );

  const baseConfig = useBaseEEConfiguration({
    vendorName,
    eePlanType,
    rootPath,
    formJson,
    validationFieldsBySidebarItem
  });

  // Genentech-copay overrides — everything that differs from the base defaults, preserving the pre-JSON
  // behavior (old result screen, modal submission, submit-on-SUBMITTED, the debit-card subtitle + card
  // result data). Only the render path changes: getFormFromJson replaces getFormSectionComponents.
  const genentechCopaySpecificConfig = {
    activeFormSection: "#enrollment-form-eligibility_questionnaire",
    fieldNames: genentechCopayFieldNames,
    getFormInitialValues: ({ applicationForm, defaultFacilityNpis }) =>
      getGenentechCopayFormInitialValues({ applicationForm, defaultFacilityNpis, formJson }),
    getFormFromJson: (form) => renderReactFromJson(form, formJson),
    applicationFormHeaderSubmission: (props) => <CompleteFormButton id={"EE_genentech_copay_submit"} {...props} />,
    headerSubmitButtonType: HEADER_SUBMIT_BUTTON_TYPES.SUBMIT_FORM,
    saveButton: (props) => <SaveButton onSave={onSaveGenentechForm} id={"EE_genentech_copay_save_form"} {...props} />,
    getShowEnrollResult: (applicationForm) => applicationForm?.status === EEsignatureStatuses.SUBMITTED,
    getEnrollResultData: ({ application }) => ({
      rxbin: application.bin,
      pcn: application.pcn,
      group: application.group,
      member_id: application.memberId,
      card_activation_date: {
        value: dateFormatter(application.approvedDate),
        style: { marginTop: "44.6px" }
      }
    }),
    getApprovedResultDescription: renderApplicationApprovedSubtitle,
    // Match the other copay vendors (regeneron/gsk/teva/pfizer/lilly all set this false): the base
    // config's getAdditionalResultCardInformation is a FUNCTION, but EnrollResultCard passes it
    // straight to t() as a key, so inheriting it rendered the raw function source on the result card.
    // The pre-JSON copay result had no such line (MVP-31021, Jehad QA — result screen).
    getAdditionalResultCardInformation: false,
    afterSubmissionPayload: () => ({}),
    isUseNewResultScreen: false,
    isModalSubmissionEnabled: true
  };

  return { ...baseConfig, ...genentechCopaySpecificConfig };
};

export default useGenentechCopayConfiguration;
